import React, { useState, useEffect } from 'react';
import { 
  X, ChevronDown, Grid, Trash2, Copy, Eye, CircleAlert,
  Star, Menu, MessageSquare, FileText, CheckSquare, AlertTriangle,
  Hash, Mail, Upload, Clock, Calendar, Image as ImageIcon, Info,
  Settings, Users, PlusCircle, Palette, Save, CheckCircle2, Layout,
  Paintbrush, Sliders, GripHorizontal, ChevronsUpDown, Wand2
} from 'lucide-react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import ImageUpload from './ImageUpload';
import GridQuestionEditor from './GridQuestionEditor';

// Predefined themes
const FORM_THEMES = [
  { id: 'classic', label: 'Classic', colors: { primary: '#2563eb', secondary: '#f3f4f6' }},
  { id: 'midnight', label: 'Midnight', colors: { primary: '#312e81', secondary: '#1e1b4b' }},
  { id: 'emerald', label: 'Emerald', colors: { primary: '#047857', secondary: '#ecfdf5' }},
  { id: 'rose', label: 'Rose', colors: { primary: '#be123c', secondary: '#ffe4e6' }},
  { id: 'amber', label: 'Amber', colors: { primary: '#d97706', secondary: '#fef3c7' }},
  { id: 'violet', label: 'Violet', colors: { primary: '#7c3aed', secondary: '#f3e8ff' }},
];

const CATEGORIES = [
  { value: 'general', label: 'General Feedback', icon: MessageSquare },
  { value: 'academic', label: 'Academic Survey', icon: FileText },
  { value: 'feedback', label: 'Customer Feedback', icon: Star },
  { value: 'event', label: 'Event Feedback', icon: Calendar },
  { value: 'other', label: 'Other', icon: Layout }
];

const QuestionTypes = [
  { 
    id: 'short_text', 
    label: 'Short Answer', 
    icon: MessageSquare,
    description: 'For brief text responses',
    preview: 'Single line text input'
  },
  { 
    id: 'long_text', 
    label: 'Paragraph', 
    icon: FileText,
    description: 'For detailed responses',
    preview: 'Multi-line text area'
  },
  { 
    id: 'single_choice', 
    label: 'Multiple Choice', 
    icon: CheckSquare,
    description: 'Select one option',
    preview: 'Radio buttons'
  },
  { 
    id: 'multiple_choice', 
    label: 'Checkboxes', 
    icon: CheckSquare,
    description: 'Select multiple options',
    preview: 'Checkbox list'
  },
  { 
    id: 'linear_scale', 
    label: 'Linear Scale', 
    icon: Menu,
    description: 'Rate on a numeric scale',
    preview: '1-5 or 1-10 scale'
  },
  { 
    id: 'rating', 
    label: 'Rating', 
    icon: Star,
    description: 'Star rating system',
    preview: '⭐⭐⭐⭐⭐'
  },
  { 
    id: 'number', 
    label: 'Number', 
    icon: Hash,
    description: 'Numeric input only',
    preview: 'Number field'
  },
  { 
    id: 'email', 
    label: 'Email', 
    icon: Mail,
    description: 'Email address input',
    preview: 'Email validation'
  },
  { 
    id: 'file_upload', 
    label: 'File Upload', 
    icon: Upload,
    description: 'Allow file uploads',
    preview: 'File uploader'
  },
  { 
    id: 'date', 
    label: 'Date', 
    icon: Calendar,
    description: 'Date selection',
    preview: 'Date picker'
  },
  { 
    id: 'dropdown', 
    label: 'Dropdown', 
    icon: ChevronDown,
    description: 'Select from a list',
    preview: 'Dropdown menu'
  },
  {
    id: 'rating_grid',
    label: 'Rating Grid',
    icon: Star,
    description: 'Rate items with stars',
    preview: 'Star rating grid'
  },
  {
    id: 'numeric_grid',
    label: 'Numeric Grid',
    icon: Grid,
    description: 'Enter numeric values in a grid',
    preview: 'Numeric input grid'
  }
];
const ImagePreview = ({ src, onRemove }) => (
  <div className="relative group">
    <img 
      src={src} 
      alt="Question preview" 
      className="w-full h-48 object-cover rounded-lg"
    />
    <button
      onClick={onRemove}
      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full 
        opacity-0 group-hover:opacity-100 transition-opacity"
    >
      <X className="h-4 w-4" />
    </button>
  </div>
);
const SurveyHeaderImage = ({ imageUrl, onChange }) => {
  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Survey Cover Image</h3>
        <span className="text-xs text-gray-500">Optional</span>
      </div>
      
      <ImageUpload
        existingImage={imageUrl}
        onImageUpload={onChange}
        className="max-w-full"
      />
      
      {imageUrl && (
        <p className="mt-2 text-xs text-gray-500">
          This image will be displayed at the top of your survey
        </p>
      )}
    </div>
  );
};
const QuestionImageUpload = ({ 
  imageUrl, 
  imageCaption, 
  onImageUpload, 
  onCaptionChange,
  required,
  onRequiredChange,
  theme 
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">Question Image</span>
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => onRequiredChange(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 
              focus:ring-blue-500 transition-colors"
          />
          <span className="ml-2 text-sm text-gray-600">Required</span>
        </label>
      </div>
      
      <ImageUpload
        existingImage={imageUrl}
        onImageUpload={onImageUpload}
        className="w-full"
      />
      
      {imageUrl && (
        <div className="space-y-2">
          <input
            type="text"
            value={imageCaption || ''}
            onChange={(e) => onCaptionChange(e.target.value)}
            placeholder="Add image caption (optional)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg
              focus:ring-2 focus:ring-blue-500 focus:border-transparent
              text-sm text-gray-700 placeholder-gray-400"
          />
          
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <ImageIcon className="h-3 w-3" />
            <span>Caption will be displayed below the image</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Toast notification component
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icons = {
    success: CheckCircle2,
    error: AlertTriangle,
    info: Info
  };

  const Icon = icons[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg flex items-center gap-2 
        ${type === 'success' ? 'bg-green-100 text-green-800' :
          type === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'}`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{message}</span>
      <button 
        onClick={onClose}
        className="ml-2 p-1 hover:bg-white/20 rounded-full"
      >
        <X className="h-4 w-4" />
      </button>
    </motion.div>
  );
};
const schedulingComponent = (formData, setFormData) => (
  <div className="space-y-6">
    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
      <Calendar className="h-5 w-5" />
      Schedule Survey
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm text-gray-700 mb-1">Start Date</label>
        <input
          type="datetime-local"
          value={formData.schedule?.startDate || ''}
                    onChange={(e) => setFormData(prev => ({
            ...prev,
            schedule: { ...prev.schedule, startDate: e.target.value }
          }))}
          className="w-full rounded-lg border-gray-300 focus:border-blue-500 
            focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-700 mb-1">End Date</label>
        <input
          type="datetime-local"
          value={formData.schedule?.endDate || ''}
                    onChange={(e) => setFormData(prev => ({
            ...prev,
            schedule: { ...prev.schedule, endDate: e.target.value }
          }))}
          className="w-full rounded-lg border-gray-300 focus:border-blue-500 
            focus:ring-blue-500"
        />
      </div>
      <div className="col-span-2">
        <label className="block text-sm text-gray-700 mb-1">Reminder Frequency</label>
        <select
          value={formData.schedule.reminderFrequency}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            schedule: { ...prev.schedule, reminderFrequency: e.target.value }
          }))}
          className="w-full rounded-lg border-gray-300 focus:border-blue-500 
            focus:ring-blue-500"
        >
          <option value="none">No Reminders</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>
    </div>
  </div>
);

// Color picker component
const ColorPicker = ({ color, onChange }) => {
  const colors = [
    '#2563eb', '#312e81', '#047857', '#be123c', '#d97706', '#7c3aed',
    '#dc2626', '#0891b2', '#4f46e5', '#c026d3', '#059669', '#ea580c'
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {colors.map((c) => (
          <button
            key={c}
            onClick={() => onChange(c)}
            className={`w-8 h-8 rounded-full transform transition-transform 
              hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 
              focus:ring-blue-500 ${color === c ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <input
        type="color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-lg cursor-pointer"
      />
    </div>
  );
};

// Question type selector with preview
const QuestionTypeSelector = ({ onSelect }) => {
  const [selectedType, setSelectedType] = useState(null);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Question Type</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {QuestionTypes.map(type => (
          <motion.button
            key={type.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setSelectedType(type.id);
              onSelect(type.id);
            }}
            className={`p-4 rounded-lg border-2 transition-all text-left
              ${selectedType === type.id 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'}`}
          >
            <div className="flex items-start gap-3">
              <type.icon className={`h-5 w-5 mt-0.5 ${
                selectedType === type.id ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <div>
                <h4 className={`font-medium ${
                  selectedType === type.id ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  {type.label}
                </h4>
                <p className="text-xs text-gray-500 mt-1">{type.description}</p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

const BatchSelector = ({ selectedBatches, onChange, theme }) => {
  // Generate batch options from 2015 to 2025
  const batches = Array.from({ length: 11 }, (_, i) => 2015 + i);
  
  const toggleBatch = (batch) => {
    if (selectedBatches.includes(batch)) {
      onChange(selectedBatches.filter(b => b !== batch));
    } else {
      onChange([...selectedBatches, batch]);
    }
  };

  const selectAll = () => onChange([]);
  const isAllSelected = selectedBatches.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">Target Batches</label>
        <button
          onClick={selectAll}
          className="text-sm"
          style={{ color: theme }}
        >
          All Batches
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={selectAll}
          className={`p-2 rounded-lg border-2 transition-all text-center
            ${isAllSelected ? 'border-0 text-white' : 'border-gray-200 text-gray-700'}`}
          style={{ 
            backgroundColor: isAllSelected ? theme : 'transparent',
            borderColor: isAllSelected ? theme : undefined
          }}
        >
          All Batches
        </motion.button>
        
        {batches.map(batch => (
          <motion.button
            key={batch}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => toggleBatch(batch)}
            className={`p-2 rounded-lg border-2 transition-all
              ${selectedBatches.includes(batch) ? 'border-0 text-white' : 'border-gray-200 text-gray-700'}`}
            style={{ 
              backgroundColor: selectedBatches.includes(batch) ? theme : 'transparent',
              borderColor: selectedBatches.includes(batch) ? theme : undefined
            }}
          >
            {batch}
          </motion.button>
        ))}
      </div>
    </div>
  );
};
const SettingsPanel = ({ settings, onSettingsChange, theme, onThemeChange, formData, setFormData, errors }) => {
  return (
    <div className="space-y-8">
      {/* Schedule Section */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 space-y-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme}15`, color: theme }}>
            <Calendar className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Schedule Survey</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date*</label>
            <div className="relative">
              <input
                type="datetime-local"
                value={formData.schedule.startDate || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  schedule: { ...prev.schedule, startDate: e.target.value }
                }))}
                className={`w-full rounded-lg border ${errors?.startDate ? 'border-red-300' : 'border-gray-300'} 
                  focus:border-blue-500 focus:ring-blue-500 pl-10`}
              />
              <Calendar 
                className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5"
                style={{ color: errors?.startDate ? '#EF4444' : theme }}
              />
              {errors?.startDate && (
                <span className="text-xs text-red-500 mt-1">{errors.startDate}</span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date*</label>
            <div className="relative">
              <input
                type="datetime-local"
                value={formData.schedule.endDate || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  schedule: { ...prev.schedule, endDate: e.target.value }
                }))}
                className={`w-full rounded-lg border ${errors?.endDate ? 'border-red-300' : 'border-gray-300'} 
                  focus:border-blue-500 focus:ring-blue-500 pl-10`}
              />
              <Calendar 
                className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5"
                style={{ color: errors?.endDate ? '#EF4444' : theme }}
              />
              {errors?.endDate && (
                <span className="text-xs text-red-500 mt-1">{errors.endDate}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Target Section */}
      <div className="bg-white rounded-xl p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme}15`, color: theme }}>
            <Users className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Target Audience</h3>
        </div>

        <BatchSelector 
          selectedBatches={formData.target.batch}
          onChange={(batches) => setFormData(prev => ({
            ...prev,
            target: { ...prev.target, batch: batches }
          }))}
          theme={theme}
        />
      </div>

      {/* Theme Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Paintbrush className="h-5 w-5" />
          Theme Customization
        </h3>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choose a theme
            </label>
            <div className="grid grid-cols-3 gap-3">
              {FORM_THEMES.map((themeOption) => (
                <button
                  key={themeOption.id}
                  onClick={() => onThemeChange(themeOption.colors.primary)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    theme === themeOption.colors.primary 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-200'
                  }`}
                >
                  <div 
                    className="w-full h-4 rounded"
                    style={{ backgroundColor: themeOption.colors.primary }}
                  />
                  <span className="text-sm mt-2 block text-center">
                    {themeOption.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom color
            </label>
            <ColorPicker color={theme} onChange={onThemeChange} />
          </div>
        </div>
      </div>
    </div>
  );
};



// Question Editor component
const QuestionEditor = ({ 
  question, 
  index, 
  updateQuestion, 
  deleteQuestion, 
  duplicateQuestion, 
  isActive, 
  onSelect,
  showValidation,
  theme
}) => {
  const Icon = QuestionTypes.find(t => t.id === question.type)?.icon || MessageSquare;
  const hasError = showValidation && !question.questionText?.trim();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      onClick={() => onSelect(question._id)}
      style={{ borderColor: isActive ? theme : undefined }}
      className={`group relative bg-white rounded-lg border-2 transition-all duration-200
        ${isActive 
          ? 'shadow-lg' 
          : 'border-gray-200 hover:border-gray-300'
        } ${hasError ? '!border-red-300' : ''}`}
    >
      <div className="absolute -left-12 top-1/2 transform -translate-y-1/2 
        opacity-0 group-hover:opacity-100 transition-opacity">
        <button className="p-2 text-gray-400 hover:text-gray-600">
          <GripHorizontal className="h-5 w-5" />
        </button>
      </div>

      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-2 rounded-lg" 
            style={{ backgroundColor: `${theme}15` }}>
            <Icon className="h-5 w-5" style={{ color: theme }} />
          </div>
          
          <div className="flex-1 space-y-4">
            <div className="relative">
              <input
                type="text"
                value={question.questionText || ''}
                onChange={(e) => updateQuestion(question._id, { questionText: e.target.value })}
                placeholder="Question"
                style={{ borderColor: isActive ? theme : 'transparent' }}
                className={`w-full text-lg border-b-2 px-0 transition-colors 
                  ${hasError ? 'border-red-300 focus:border-red-500' : 
                    'hover:border-gray-300 focus:border-current'}`}
              />
              {hasError && (
                <div className="absolute right-0 top-0 text-red-500 text-sm flex items-center">
                  <CircleAlert className="h-4 w-4 mr-1" />
                  Required
                </div>
              )}
            </div>

            {/* Question Content */}
            {['single_choice', 'multiple_choice', 'dropdown'].includes(question.type) && (
              <div className="space-y-3">
                {(question.options || []).map((option, optionIndex) => (
                  <motion.div
                    key={optionIndex}
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="flex items-center gap-3 group/option"
                  >
                    <div style={{ borderColor: isActive ? theme : undefined }}
                      className={`w-4 h-4 border-2 transition-colors
                      ${question.type === 'multiple_choice' ? 'rounded-sm' : 'rounded-full'}
                      ${!isActive && 'border-gray-300'}`}
                    />
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...question.options];
                        newOptions[optionIndex] = e.target.value;
                        updateQuestion(question._id, { options: newOptions });
                      }}
                      placeholder={`Option ${optionIndex + 1}`}
                      className="flex-1 px-3 py-2 bg-gray-50 rounded-lg border-0 
                        focus:ring-0 focus:bg-gray-100 transition-colors"
                    />
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        const newOptions = question.options.filter((_, i) => i !== optionIndex);
                        updateQuestion(question._id, { options: newOptions });
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg 
                        opacity-0 group-hover/option:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </motion.div>
                ))}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newOptions = [...(question.options || []), `Option ${question.options.length + 1}`];
                    updateQuestion(question._id, { options: newOptions });
                  }}
                  style={{ color: theme }}
                  className="flex items-center gap-2 text-sm font-medium transition-colors pl-6 hover:opacity-80"
                >
                  <PlusCircle className="h-4 w-4" />
                  <span>Add option</span>
                </button>
              </div>
            )}

            {['linear_scale', 'rating'].includes(question.type) && (
              <div className="space-y-4">
                <div className="flex items-center gap-6">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Min value</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={question.settings?.minValue || 0}
                      onChange={(e) => updateQuestion(question._id, {
                        settings: { ...question.settings, minValue: parseInt(e.target.value) }
                      })}
                      style={{ borderColor: isActive ? theme : undefined }}
                      className="w-20 px-2 py-1 border rounded-lg focus:ring-1 focus:ring-current"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Max value</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={question.settings?.maxValue || 5}
                      onChange={(e) => updateQuestion(question._id, {
                        settings: { ...question.settings, maxValue: parseInt(e.target.value) }
                      })}
                      style={{ borderColor: isActive ? theme : undefined }}
                      className="w-20 px-2 py-1 border rounded-lg focus:ring-1 focus:ring-current"
                    />
                  </div>
                </div>
                {question.type === 'rating' && (
                  <div className="flex items-center gap-1">
                    {Array.from({ length: question.settings?.maxValue || 5 }).map((_, i) => (
                      <Star 
                        key={i} 
                        className="h-5 w-5" 
                        style={{ color: theme }} 
                        fill={theme}
                      />
                    ))}
                  </div>
                )}
             {question.type === 'image' && (
  <QuestionImageUpload
    imageUrl={question.imageUrl}
    imageCaption={question.imageCaption}
    required={question.settings?.required || false}
    onImageUpload={(url) => updateQuestion(question._id, { imageUrl: url })}
    onCaptionChange={(caption) => updateQuestion(question._id, { 
      imageCaption: caption 
    })}
    onRequiredChange={(required) => updateQuestion(question._id, {
      settings: { ...question.settings, required }
    })}
    theme={theme}
  />
)}
              </div>
            )}
{['rating_grid', 'numeric_grid'].includes(question.type) && (
  <GridQuestionEditor
    question={question}
    updateQuestion={updateQuestion}
    isActive={isActive}
    theme={theme}
  />
)}
            {isActive && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between pt-4 border-t"
              >
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={question.required}
                      onChange={(e) => updateQuestion(question._id, { required: e.target.checked })}
                      style={{ 
                        borderColor: theme,
                        backgroundColor: question.required ? theme : 'white'
                      }}
                      className="rounded border-2 transition-colors focus:ring-offset-2 focus:ring-current"
                    />
                    Required
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateQuestion(question._id);
                      }}
                      style={{ 
                        '--hover-bg': `${theme}15`,
                        '--hover-color': theme 
                      }}
                      className="p-1.5 text-gray-400 hover:text-[var(--hover-color)] rounded-lg 
                        hover:bg-[var(--hover-bg)] transition-all"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteQuestion(question._id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg 
                        hover:bg-red-50 transition-all"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => {/* Toggle advanced settings */}}
                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  <Sliders className="h-4 w-4" />
                  Advanced
                </button>
              </motion.div>
            )}


          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Main Survey Creator Component
const CreateSurveyModal = ({ isOpen, onClose, selectedSurvey, onSuccess }) => {
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [showSettings, setShowSettings] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
// First, update the initial form data state with target properly initialized
const [formData, setFormData] = useState({
  title: '',
  description: '',
  type: 'survey',
  category: 'general',
  questions: [],
  settings: {
    allowAnonymous: false,
    requireAuthentication: true,
    showProgressBar: true,
    theme: FORM_THEMES[0].colors.primary,
    shuffleQuestions: false,
  },
  surveyImage: '',
  schedule: {
    startDate: '',
    endDate: '',
    reminderFrequency: 'none'
  },
  target: {
    batch: [],  // Initialize with empty array
    year: [],   // Initialize with empty array
    specific: [] // Initialize with empty array
  }
});

useEffect(() => {
  if (isOpen) {
    if (selectedSurvey) {
      const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toISOString().slice(0, 16);
      };

      setFormData({
        ...selectedSurvey,
        schedule: {
          ...selectedSurvey.schedule,
          startDate: formatDate(selectedSurvey.schedule?.startDate),
          endDate: formatDate(selectedSurvey.schedule?.endDate)
        },
        target: {
          batch: selectedSurvey.target?.batch || [],
          year: selectedSurvey.target?.year || [],
          specific: selectedSurvey.target?.specific || []
        }
      });
    } else {
      // Reset to initial state when creating new survey
      setFormData({
        title: '',
        description: '',
        type: 'survey',
        category: 'general',
        questions: [],
        settings: {
          allowAnonymous: false,
          requireAuthentication: true,
          showProgressBar: true,
          theme: FORM_THEMES[0].colors.primary,
          shuffleQuestions: false
        },
        schedule: {
          startDate: '',
          endDate: '',
          reminderFrequency: 'none'
        },
        target: {
          batch: [],
          year: [],
          specific: []
        }
      });
    }
    setShowValidation(false);
    setErrors({});
    setActiveQuestionId(null);
    setShowSettings(true);
  }
}, [isOpen, selectedSurvey]);

  const updateQuestion = (questionId, updates) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q =>
        q._id === questionId ? { ...q, ...updates } : q
      )
    }));
  };
  const handleSave = async () => {
    try {
      setSaving(true);
      setShowValidation(true);
      
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setToast({
          type: 'error',
          message: 'Please fix the validation errors before saving'
        });
        return;
      }

      const cleanedQuestions = formData.questions.map(question => ({
        questionText: question.questionText?.trim() || '',
        type: question.type,
        required: question.required || false,
        options: Array.isArray(question.options)
          ? question.options.filter(opt => opt && opt.trim() !== '')
          : [],
        settings: {
          ...question.settings,
          minValue: question.settings?.minValue || null,
          maxValue: question.settings?.maxValue || null
        }
      }));

      const dataToSend = {
        ...formData,
        title: formData.title.trim(),
        category: formData.category,
        questions: cleanedQuestions,
        settings: {
          ...formData.settings
        },
        schedule: {
          startDate: formData.schedule.startDate || null,
          endDate: formData.schedule.endDate || null,
          reminderFrequency: formData.schedule.reminderFrequency || 'none'
        },
        target: {
          batch: formData.target.batch || [],
          year: formData.target.year || [],
          specific: formData.target.specific || []
        }
      };

      const url = selectedSurvey
        ? `${process.env.REACT_APP_BASE_URL}/api/surveys/surveys/${selectedSurvey._id}`
        : `${process.env.REACT_APP_BASE_URL}/api/surveys/surveys`;

      const response = await fetch(url, {
        method: selectedSurvey ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save survey');
      }

      setToast({
        type: 'success',
        message: `Survey ${selectedSurvey ? 'updated' : 'created'} successfully!`
      });
      onSuccess(data);
      onClose();
    } catch (error) {
      console.error('Error saving survey:', error);
      setToast({
        type: 'error',
        message: error.message || 'Failed to save survey'
      });
    } finally {
      setSaving(false);
    }
  };
  const deleteQuestion = (questionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q._id !== questionId)
    }));
  };
  const duplicateQuestion = (questionId) => {
    const question = formData.questions.find(q => q._id === questionId);
    if (question) {
      const duplicate = {
        ...question,
        _id: crypto.randomUUID(),
        questionText: `${question.questionText || ''} (Copy)`
      };
      setFormData(prev => ({
        ...prev,
        questions: [...prev.questions, duplicate]
      }));
    }
  };

  if (!isOpen) return null;  
  // Modified add question workflow
  const addQuestion = (type) => {
    const newQuestion = {
      _id: crypto.randomUUID(),
      questionText: '',
      type,
      required: false,
      settings: {
        gridRows: ['rating_grid', 'numeric_grid'].includes(type) ? [] : undefined,
        gridColumns: ['rating_grid', 'numeric_grid'].includes(type) ? [] : undefined,
        gridType: type === 'rating_grid' ? 'rating' : 'numeric',
        maxRating: type === 'rating_grid' ? 5 : undefined
      }
    };
    
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
    setActiveQuestionId(newQuestion._id);
    setIsAddingQuestion(false);
  };

  // Updated validation
  const validateForm = () => {
    const newErrors = {};
    const errorMessages = [];
    
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
      errorMessages.push('Title is required');
    }
    
    if (!formData.schedule.startDate) {
      newErrors.startDate = 'Start date is required';
      errorMessages.push('Start date is required');
    }
    
    if (!formData.schedule.endDate) {
      newErrors.endDate = 'End date is required';
      errorMessages.push('End date is required');
    }
  
    if (formData.schedule.startDate && formData.schedule.endDate) {
      const start = new Date(formData.schedule.startDate);
      const end = new Date(formData.schedule.endDate);
      if (end <= start) {
        newErrors.schedule = 'End date must be after start date';
        errorMessages.push('End date must be after start date');
      }
    }
    
    if (!formData.questions?.length) {
      newErrors.questions = 'At least one question is required';
      errorMessages.push('At least one question is required');
    }
  
    // Show error toast with all validation messages
    if (errorMessages.length > 0) {
      setToast({
        type: 'error',
        message: (
          <div className="space-y-1">
            <p className="font-medium">Please fix the following errors:</p>
            <ul className="list-disc list-inside">
              {errorMessages.map((msg, i) => (
                <li key={i} className="text-sm">{msg}</li>
              ))}
            </ul>
          </div>
        )
      });
    }
    
    setErrors(newErrors);
    return newErrors;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="min-h-screen px-4 text-center">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="inline-block w-full max-w-6xl my-8 text-left align-middle 
                bg-gray-50 rounded-2xl shadow-xl relative"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b 
                rounded-t-2xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex items-center gap-4">
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, title: e.target.value }));
                        if (errors.title) setErrors(prev => ({ ...prev, title: null }));
                      }}
                      placeholder="Untitled Survey"
                      className={`text-2xl font-semibold border-0 focus:ring-0 w-full 
                        placeholder-gray-400 ${errors.title ? 'text-red-500' : ''}`}
                    />
                    
                    <select
                      value={formData.category}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, category: e.target.value }));
                        if (errors.category) setErrors(prev => ({ ...prev, category: null }));
                      }}
                      className={`rounded-lg border-gray-300 ${
                        errors.category ? 'border-red-300' : ''
                      }`}
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowSettings(!showSettings)}
                      className={`p-2 rounded-lg transition-colors ${
                        showSettings 
                          ? 'bg-blue-50 text-blue-600' 
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                   
                    <button 
                      onClick={onClose}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {(errors.title || errors.category) && (
                  <div className="mt-2 text-sm text-red-500">
                    {errors.title || errors.category}
                  </div>
                )}
              </div>

              {/* Main Content */}
              <div className="flex">
                <div className={`transition-all duration-300 ${
                  showSettings ? 'w-2/3' : 'w-full'}`}>
                  <div className="p-6">
                    {/* Description */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                    <SurveyHeaderImage
      imageUrl={formData.surveyImage}
      onChange={(url) => setFormData(prev => ({ 
        ...prev, 
        surveyImage: url 
      }))}
    />
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          description: e.target.value 
                        }))}
                        placeholder="Add a description to help people understand your survey..."
                        rows={2}
                        className="w-full border-0 focus:ring-0 resize-none text-gray-700
                          placeholder-gray-400"
                      />
                    </div>
  
                    {/* Questions */}
                    <div className="mt-6 space-y-6">
                      {formData.questions.map((question, index) => (
                        <QuestionEditor
                          key={question._id}
                          question={question}
                          index={index}
                          updateQuestion={updateQuestion}
                          deleteQuestion={deleteQuestion}
                          duplicateQuestion={duplicateQuestion}
                          isActive={activeQuestionId === question._id}
                          onSelect={setActiveQuestionId}
                          showValidation={showValidation}
                          theme={formData.settings.theme}
                        />
                      ))}
                      
                      {/* Add Question Button */}
                      {!isAddingQuestion ? (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setIsAddingQuestion(true)}
                          className="w-full p-4 rounded-lg border-2 border-dashed border-gray-300 
                            hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center 
                            justify-center gap-2 text-gray-500 hover:text-blue-600 group"
                        >
                          <PlusCircle className="h-5 w-5" />
                          <span className="font-medium">Add Question</span>
                        </motion.button>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className="border border-gray-200 rounded-lg bg-white shadow-lg p-6"
                        >
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Add Question</h3>
                            <button
                              onClick={() => setIsAddingQuestion(false)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            {QuestionTypes.map(type => (
                              <motion.button
                                key={type.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => addQuestion(type.id)}
                                className="p-4 rounded-lg border border-gray-200 hover:border-blue-500 
                                  hover:bg-blue-50 transition-all text-left"
                              >
                                <div className="flex items-center gap-3">
                                  <type.icon className="h-5 w-5 text-gray-400" />
                                  <div>
                                    <h4 className="font-medium text-gray-900">{type.label}</h4>
                                    <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                                  </div>
                                </div>
                              </motion.button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>
  
                {/* Settings Panel */}
                {showSettings && (
  <div className="w-1/3 border-l bg-white h-full p-6">
    <SettingsPanel
      settings={formData.settings}
      onSettingsChange={(key, value) => setFormData(prev => ({
        ...prev,
        settings: { ...prev.settings, [key]: value }
      }))}
      theme={formData.settings.theme}
      onThemeChange={(color) => setFormData(prev => ({
        ...prev,
        settings: { ...prev.settings, theme: color }
      }))}
      formData={formData}
      setFormData={setFormData}
      errors={errors}
    />
  </div>
)}
              </div>
  
              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t px-6 py-4 rounded-b-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="h-4 w-4 mr-2" />
                    Last edited {new Date().toLocaleTimeString()}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg
                        transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className={`px-6 py-2 text-white rounded-lg flex items-center gap-2
                        transition-colors ${
                          saving 
                            ? 'opacity-75 cursor-not-allowed' 
                            : 'hover:bg-blue-700'
                        }`}
                      style={{ backgroundColor: formData.settings.theme }}
                    >
                      {saving && (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-4 h-4 border-2 border-white border-t-transparent 
                            rounded-full"
                        />
                      )}
                      {saving ? 'Saving...' : selectedSurvey ? 'Update Survey' : 'Create Survey'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
  
            {/* Toast Notifications */}
            <AnimatePresence>
              {toast && (
                <Toast
                  message={toast.message}
                  type={toast.type}
                  onClose={() => setToast(null)}
                />
              )}
            </AnimatePresence>
          </div>
</div>
        )}
        </AnimatePresence>

    );
  };
  
  CreateSurveyModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedSurvey: PropTypes.shape({
      _id: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      type: PropTypes.string,
      category: PropTypes.string,
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          questionText: PropTypes.string,
          type: PropTypes.string,
          required: PropTypes.bool,
          options: PropTypes.arrayOf(PropTypes.string),
          settings: PropTypes.object
        })
      ),
      settings: PropTypes.object,
      schedule: PropTypes.object
    }),
    onSuccess: PropTypes.func.isRequired
  };
  
  export default CreateSurveyModal;